import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, MoreVertical, Edit2, Trash2, X, Save, Share2, Lock, LockKeyhole, Boxes, GitMerge, Shapes } from "lucide-react";

import { Structure, api } from "../lib/api";
import { useTheme } from "../lib/theme";
import { useStructureTypes } from "../lib/useStructureTypes";
import { PasswordModal } from "../components/PasswordModal";
import { ConfirmModal } from "../components/ConfirmModal";
import { WaveBackground } from "../components/WaveBackground";
import { CreateStructureModal } from "../components/CreateStructureModal";
import { StatCard } from "../components/StatCard";
import { Spinner } from "../components/Spinner";
import { InlineAlert } from "../components/InlineAlert";

export const StructuresPage: React.FC = () => {
  const [items, setItems] = useState<Structure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [passwordModal, setPasswordModal] = useState<{
    isOpen: boolean;
    structureId: string;
    structureName: string;
    mode: "private" | "edit";
    shareToken?: string | null;
  } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; name: string; requiresPassword: boolean } | null>(null);
  const [modeChoice, setModeChoice] = useState<Structure | null>(null);
  const [stats, setStats] = useState<{ loading: boolean; nodes: number; edges: number; types: number; error: string | null }>({
    loading: false,
    nodes: 0,
    edges: 0,
    types: 0,
    error: null,
  });
  const [structureStats, setStructureStats] = useState<Record<string, { nodes: number; edges: number; types: number }>>({});
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { types: structureTypes } = useStructureTypes();

  useEffect(() => {
    api
      .listStructures()
      .then((res) => setItems(res.items))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loading) return;
    if (items.length === 0) {
      setStats({ loading: false, nodes: 0, edges: 0, types: 0, error: null });
      setStructureStats({});
      return;
    }
    let cancelled = false;
    setStats((prev) => ({ ...prev, loading: true, error: null }));

    const loadStats = async () => {
      try {
        const results = await Promise.allSettled(items.map((s) => api.getGraph(s.id)));
        let nodes = 0;
        let edges = 0;
        const types = new Set<string>();
        const perStructure: Record<string, { nodes: number; edges: number; types: number }> = {};
        results.forEach((res, idx) => {
          if (res.status === "fulfilled") {
            nodes += res.value.nodes.length;
            edges += res.value.edges.length;
            res.value.nodes.forEach((n) => types.add(n.type));
            perStructure[items[idx].id] = {
              nodes: res.value.nodes.length,
              edges: res.value.edges.length,
              types: new Set(res.value.nodes.map((n) => n.type)).size,
            };
          }
        });
        if (cancelled) return;
        setStats({ loading: false, nodes, edges, types: types.size, error: null });
        setStructureStats(perStructure);
      } catch (err: any) {
        if (cancelled) return;
        setStats({ loading: false, nodes: 0, edges: 0, types: 0, error: err.message || "Statistiken konnten nicht geladen werden" });
        setStructureStats({});
      }
    };

    loadStats();
    return () => {
      cancelled = true;
    };
  }, [items, loading]);

  useEffect(() => {
    const handleClickOutside = () => setMenuOpen(null);
    if (menuOpen) {
      window.addEventListener("click", handleClickOutside);
      return () => window.removeEventListener("click", handleClickOutside);
    }
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen && !items.some((s) => s.id === menuOpen)) {
      setMenuOpen(null);
    }
  }, [items, menuOpen]);

  const loadStructures = async () => {
    setLoading(true);
    try {
      const res = await api.listStructures();
      setItems(res.items);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStructure = async (name: string, description: string, structureTypeKey: string) => {
    setCreating(true);
    try {
      const struct = await api.createStructure({
        name,
        description: description || "",
        tags: [],
        structure_type_key: structureTypeKey
      });
      if (!struct.share_enabled || !struct.share_token) {
        try {
          await api.enableShare(struct.id);
        } catch (err) {
          console.warn("Share default activation failed", err);
        }
      }
      navigate(`/s/${struct.id}`, { state: { autoOpenSettings: true } });
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (s: Structure) => {
    setEditingId(s.id);
    setEditName(s.name);
    setEditDescription(s.description || "");
    setMenuOpen(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditDescription("");
  };

  const saveEdit = async (id: string) => {
    try {
      const updated = await api.updateStructure(id, { name: editName, description: editDescription });
      setItems((prev) => prev.map((s) => (s.id === id ? updated : s)));
      setEditingId(null);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const performDelete = async (id: string) => {
    try {
      await api.deleteStructure(id);
      setItems((prev) => prev.filter((s) => s.id !== id));
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
    setMenuOpen(null);
  };

  const requestDelete = (structure: Structure) => {
    setMenuOpen(null);
    setConfirmDelete({
      id: structure.id,
      name: structure.name,
      requiresPassword: !!structure.edit_password_enabled,
    });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const { id, name, requiresPassword } = confirmDelete;
    setConfirmDelete(null);
    if (requiresPassword) {
      setPendingDelete({ id, name });
      setPasswordModal({
        isOpen: true,
        structureId: id,
        structureName: name,
        mode: "edit",
        shareToken: items.find((s) => s.id === id)?.share_token || null,
      });
      return;
    }
    await performDelete(id);
  };

  const handleStructureClick = (e: React.MouseEvent, structure: Structure) => {
    e.preventDefault();

    // If structure is private, always require password
    if (!structure.share_enabled) {
      setPasswordModal({
        isOpen: true,
        structureId: structure.id,
        structureName: structure.name,
        mode: "private",
      });
      return;
    }

    // If structure is public but has no edit password, ask for view or edit
    if (structure.share_enabled && !structure.edit_password_enabled) {
      setModeChoice(structure);
      return;
    }

    // Public + edit-protected: offer password for edit or view-only fallback
    setPasswordModal({
      isOpen: true,
      structureId: structure.id,
      structureName: structure.name,
      mode: "edit",
      shareToken: structure.share_token || null,
    });
  };

  const handlePasswordSubmit = async (password: string) => {
    if (!passwordModal) return;

    try {
      await api.authenticate(passwordModal.structureId, password);
      setPasswordModal(null);
      if (pendingDelete) {
        await performDelete(pendingDelete.id);
        setPendingDelete(null);
      } else {
        navigate(`/s/${passwordModal.structureId}`);
      }
    } catch (err: any) {
      // Error will be shown by PasswordModal component
      throw err;
    }
  };

  const openViewMode = async (structure: Structure) => {
    let token = structure.share_token || null;
    if (!token) {
      try {
        const res = await api.enableShare(structure.id);
        token = res.share_token;
        setItems((prev) =>
          prev.map((s) => (s.id === structure.id ? { ...s, share_enabled: true, share_token: token } : s))
        );
      } catch (err: any) {
        setError(err.message || "Freigabe fehlgeschlagen");
        return;
      }
    }
    if (!token) {
      setError("Kein Share-Link verfügbar");
      return;
    }
    navigate(`/share/${token}`);
  };

  const statCards = [
    {
      label: "Nodes",
      value: stats.loading ? <Spinner size="sm" className="text-[var(--color-muted)]" /> : stats.nodes,
      icon: <Boxes className="h-4 w-4 sm:h-5 sm:w-5" />,
      tone: "blue" as const,
    },
    {
      label: "Edges",
      value: stats.loading ? <Spinner size="sm" className="text-[var(--color-muted)]" /> : stats.edges,
      icon: <GitMerge className="h-4 w-4 sm:h-5 sm:w-5" />,
      tone: "emerald" as const,
    },
    {
      label: "Typen",
      value: stats.loading ? <Spinner size="sm" className="text-[var(--color-muted)]" /> : stats.types,
      icon: <Shapes className="h-4 w-4 sm:h-5 sm:w-5" />,
      tone: "purple" as const,
    },
    {
      label: "Strukturen",
      value: items.length,
      icon: <Share2 className="h-4 w-4 sm:h-5 sm:w-5" />,
      tone: "slate" as const,
    },
  ];

  return (
    <div className="min-h-screen relative transition-colors">
      {theme === "light" && <WaveBackground />}
      {theme === "dark" && (
        <div className="fixed inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" style={{ zIndex: 0 }} />
      )}

      {/* Hero Section */}
      <header className="relative px-4 sm:px-8 py-8 md:py-12" style={{ zIndex: 1 }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6 mb-8 md:mb-12">
            <div className="flex-1">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black title-font mb-3 text-slate-900 dark:text-white" style={{ textShadow: '0 0 8px rgba(255,255,255,0.9), 0 0 16px rgba(255,255,255,0.7), 0 0 2px rgba(255,255,255,1)' }}>
                Atlas
              </h1>
              <p className="text-base md:text-lg text-slate-800 dark:text-slate-300 subtitle-font max-w-2xl font-semibold" >
                Organisationsstrukturen, Prozesse und Zusammenhänge visualisieren und verwalten
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <button className="btn btn-secondary flex-1 sm:flex-initial" onClick={toggle} aria-label="Theme toggle">
                {theme === "dark" ? "Light" : "Dark"}
              </button>
              <button className="btn btn-primary flex-1 sm:flex-initial" onClick={() => setShowCreateModal(true)} disabled={creating}>
                {creating ? <Spinner size="sm" /> : <Plus className="h-4 w-4" />}
                Neue Struktur
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative px-4 sm:px-8 pb-16" style={{ zIndex: 1 }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-6">
            {statCards.map((stat) => (
              <StatCard key={stat.label} label={stat.label} value={stat.value} icon={stat.icon} tone={stat.tone} />
            ))}
          </div>
          {stats.error && (
            <InlineAlert
              tone="warning"
              message="Statistiken konnten nicht vollständig geladen werden."
              actionLabel="Erneut laden"
              onAction={loadStructures}
              className="mb-4"
            />
          )}
          {error && (
            <InlineAlert
              tone="error"
              title="Strukturen konnten nicht geladen werden"
              message={error}
              onAction={loadStructures}
              actionLabel="Nochmal versuchen"
              className="mb-6"
            />
          )}
          {loading ? (
            <Spinner label="Lädt Strukturen..." className="flex items-center justify-center py-12" />
          ) : (
            <div className="grid gap-5 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((s) => (
              <div key={s.id} className="card p-6 relative group hover:shadow-2xl transition-all duration-200 hover:-translate-y-1">
                {editingId === s.id ? (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-slate-600 dark:text-slate-400">Name</label>
                      <input
                        className="w-full mt-1 px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-700"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 dark:text-slate-400">Beschreibung</label>
                      <textarea
                        className="w-full mt-1 px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-700"
                        rows={3}
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        className="btn btn-primary flex items-center gap-1"
                        onClick={() => saveEdit(s.id)}
                        disabled={editName.trim().length === 0}
                      >
                        <Save className="h-4 w-4" /> Speichern
                      </button>
                      <button className="btn btn-secondary" onClick={cancelEdit}>
                        <X className="h-4 w-4" /> Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between">
                      <a
                        href={`/s/${s.id}`}
                        onClick={(e) => handleStructureClick(e, s)}
                        className="flex-1 hover:opacity-80 transition-opacity cursor-pointer"
                      >
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h2 className="font-semibold text-lg">{s.name}</h2>
                        {(() => {
                          const structureType = structureTypes.find(t => t.key === s.structure_type_key);
                          if (structureType) {
                            return (
                              <span
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-700"
                                title={`Struktur-Typ: ${structureType.label}`}
                              >
                                {structureType.icon}
                                {structureType.label}
                              </span>
                            );
                          }
                          return null;
                        })()}
                        {s.share_enabled ? (
                          <span
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700"
                            title="Öffentlich zugänglich - jeder mit dem Link kann diese Struktur ansehen"
                          >
                              <Share2 className="h-3 w-3" />
                              Geteilt
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-700"
                              title="Privat - Passwort erforderlich für Ansicht und Bearbeitung"
                            >
                              <Lock className="h-3 w-3" />
                              Privat
                          </span>
                        )}
                        {s.edit_password_enabled && (
                          <span
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-700"
                            title="Bearbeitung geschützt - Passwort erforderlich zum Bearbeiten"
                          >
                            <LockKeyhole className="h-3 w-3" />
                            Edit geschützt
                          </span>
                        )}
                      </div>
                      {s.description && <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">{s.description}</p>}
                      <div className="mt-2 flex items-center gap-3 text-xs text-[var(--color-muted)]">
                        <span className="inline-flex items-center gap-1">
                          <Boxes className="h-3 w-3" /> {(structureStats[s.id]?.nodes ?? "–").toString()}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <GitMerge className="h-3 w-3" /> {(structureStats[s.id]?.edges ?? "–").toString()}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Shapes className="h-3 w-3" /> {(structureStats[s.id]?.types ?? "–").toString()}
                        </span>
                      </div>
                      <div className="mt-3 flex gap-2 flex-wrap">
                        {s.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800">
                            {tag}
                          </span>
                          ))}
                        </div>
                      </a>
                      <div className="relative">
                        <button
                          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpen(menuOpen === s.id ? null : s.id);
                          }}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                        {menuOpen === s.id && (
                          <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-10">
                            <button
                              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                startEdit(s);
                              }}
                            >
                              <Edit2 className="h-4 w-4" /> Umbenennen
                            </button>
                            <button
                              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                requestDelete(s);
                              }}
                            >
                              <Trash2 className="h-4 w-4" /> Löschen
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
            {items.length === 0 && (
              <div className="col-span-full text-center py-16">
                <div className="text-slate-600 dark:text-slate-400 text-lg mb-4">
                  Noch keine Strukturen vorhanden
                </div>
                <p className="text-slate-500 dark:text-slate-500 text-sm">
                  Starte mit "Neue Struktur" um deine erste Organisationsstruktur oder deinen ersten Prozess zu erstellen
                </p>
              </div>
            )}
          </div>
        )}
        </div>
      </main>

      {/* Password Modal */}
      {passwordModal && (
        <PasswordModal
          isOpen={passwordModal.isOpen}
          onClose={() => setPasswordModal(null)}
          onSubmit={handlePasswordSubmit}
          mode={passwordModal.mode}
          structureName={passwordModal.structureName}
          allowCancel={true}
          showViewOption={passwordModal.mode === "edit" && !!passwordModal.shareToken && !pendingDelete}
          onViewOnly={
            passwordModal.shareToken && !pendingDelete
              ? () => {
                  setPasswordModal(null);
                  navigate(`/share/${passwordModal.shareToken}`);
                }
              : undefined
          }
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          isOpen={true}
          title={`Struktur "${confirmDelete.name}" löschen?`}
          description="Dieser Vorgang kann nicht rückgängig gemacht werden."
          confirmLabel="Löschen"
          cancelLabel="Abbrechen"
          tone="danger"
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {modeChoice && (
        <ConfirmModal
          isOpen={true}
          title={`"${modeChoice.name}" öffnen`}
          description="Bearbeiten oder im View-Modus ansehen?"
          confirmLabel="Bearbeiten"
          secondaryLabel="View-Modus"
          cancelLabel="Abbrechen"
          onConfirm={() => {
            setModeChoice(null);
            navigate(`/s/${modeChoice.id}`);
          }}
          onSecondary={() => {
            const target = modeChoice;
            setModeChoice(null);
            void openViewMode(target);
          }}
          onCancel={() => setModeChoice(null)}
        />
      )}

      <CreateStructureModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateStructure}
      />
    </div>
  );
};
